import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import { usePagesTranslation } from "../locales";
import AuthService from "../../services/AuthService";
import { NATIVE_IOS_ANDROID_AVAILABLE } from "../../config/nativeAppsVisibility";
import { buildDeletionStatusUrl } from "../login/data-deletion/deletionStatusRoutes";

/**
 * Dialog for requesting deletion of all user data.
 * Shows informational message and submits the request.
 * @param {boolean} hasActiveSubscription - If true, shows subscription cancellation and no-refund warning
 */
const DataDeletionRequestDialog = ({ open, onClose, onSubmit, submitting, hasActiveSubscription }) => {
    const { t } = usePagesTranslation();
    const [result, setResult] = useState(null);

    const handleRequest = async () => {
        try {
            const res = await onSubmit();
            setResult(res);
        } catch (err) {
            setResult({ error: err.message });
        }
    };

    const handleClose = async () => {
        const wasSuccess = result && !result.error;
        setResult(null);
        onClose();
        if (wasSuccess) {
            await AuthService.logout();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t("profile.dataDeletion.dialogTitle")}
            </DialogTitle>
            <DialogContent>
                {!result ? (
                    <>
                        <Typography variant="body1" paragraph>
                            {t("profile.dataDeletion.dialogMessage1")}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {t(
                                NATIVE_IOS_ANDROID_AVAILABLE
                                    ? "profile.dataDeletion.dialogMessage2"
                                    : "profile.dataDeletion.dialogMessage2-web"
                            )}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {t("profile.dataDeletion.dialogMessage3")}
                        </Typography>
                        {hasActiveSubscription && (
                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }} paragraph>
                                {t("profile.dataDeletion.subscriptionWarning")}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                            {t("core.privacy.s6RetentionBody")}
                        </Typography>
                    </>
                ) : result.error ? (
                    <Typography variant="body1" color="error" paragraph>
                        {result.error}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body1" paragraph>
                            {t("profile.dataDeletion.successMessage")}
                        </Typography>
                        {(result.confirmationCode || result.confirmation_code) && (
                            <Typography variant="body2" paragraph>
                                <strong>{t("profile.dataDeletion.confirmationCode")}:</strong>{" "}
                                {result.confirmationCode || result.confirmation_code}
                            </Typography>
                        )}
                        <Typography variant="body2" paragraph>
                            {t("profile.dataDeletion.checkStatus")}
                        </Typography>
                        {(() => {
                            const confirmationCode = result.confirmationCode || result.confirmation_code;
                            const statusUrl = buildDeletionStatusUrl(confirmationCode);
                            return (
                                <Typography
                                    variant="body2"
                                    component="a"
                                    href={statusUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: "block", wordBreak: "break-all", color: "primary.main" }}
                                >
                                    {statusUrl}
                                </Typography>
                            );
                        })()}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                {!result || result.error ? (
                    <>
                        <Button onClick={handleClose} variant="outlined">
                            {t("profile.dataDeletion.cancel")}
                        </Button>
                        <Button
                            onClick={handleRequest}
                            variant="contained"
                            color="error"
                            disabled={submitting}
                            sx={{ backgroundColor: "#3BA666", "&:hover": { backgroundColor: "#2E8B57" } }}
                        >
                            {submitting ? t("profile.dataDeletion.submitting") : t("profile.dataDeletion.request")}
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleClose} variant="contained">
                        {t("profile.dataDeletion.done")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DataDeletionRequestDialog;
